import React, { FC, useState } from 'react';
import { Dayjs } from 'dayjs';
import { FilterSelect } from '@care/provider-react-components';
import { ChildCareAgeGroups } from '@care/caregiver-profile-components';
import { FilterSelectProps } from '@care/provider-react-components/dist/components/Filter/components/FilterSelect/FilterSelect';

import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import {
  ActivityParticipationType,
  DayOfWeek,
  SEOSegmentationFilterInput,
} from '@/__generated__/globalTypes';
import { saveFilters } from '@/utils/filtersStorage';
import { useSegmentationFiltersContext } from '@/components/contexts/SegmentationFiltersContext';
import {
  activityTypes,
  ages,
  days,
  FilterFieldNames,
  DAYS_MAP_TO_FILTER,
  ACTIVITY_MAP_TO_FILTER,
  AGES_MAP_TO_FILTER,
  ACTIVITY_CATEGORY_TITLES,
  ACTIVITY_CATEGORY_METADATA,
  ACTIVITY_CATEGORY_TO_KEY,
  ActivityCategory,
} from '@/constants';
import { seoActivitySegmentationListingPage_seoSegmentationActivityDetails_SEOSegmentationActivityDetailsSuccess_segmentDetails as ActivityDetails } from '@/__generated__/seoActivitySegmentationListingPage';

import { FilterLayout } from './FilterLayout';

export interface ExploreFilterProps {
  activityCategory: ActivityDetails['category'];
  activitySubcategory: ActivityDetails['subcategory'];
  isMobile: boolean;
  LocationInput: React.ReactNode;
}

export const MultiSelect: React.FC<Omit<FilterSelectProps, 'type'>> = (props) => {
  return <FilterSelect type="multi-select" triggerSx={{ maxWidth: 250 }} {...props} />;
};

export const getCategoryKey = (category: string): ActivityCategory => {
  // Convert camelCase to snake_case and then to lowercase
  const snakeCaseCategory = category
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/ /g, '_');
  return ACTIVITY_CATEGORY_TO_KEY[snakeCaseCategory] ?? '';
};

const toTitleCase = (str: string) => {
  const acronyms = ['DJ', '3D'];
  const titleCased = str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );
  return titleCased
    .split(' ')
    .map((word) => {
      if (acronyms.includes(word.toUpperCase())) {
        return word.toUpperCase();
      }
      return word;
    })
    .join(' ');
};

const getSubcategories = (categoryKey: ActivityCategory) => {
  const subcategoryMetadata =
    ACTIVITY_CATEGORY_METADATA[categoryKey]?.subcategories ??
    Object.values(ACTIVITY_CATEGORY_METADATA).flatMap((category) => category.subcategories);
  return subcategoryMetadata
    .map((subcategory) => toTitleCase(subcategory.name))
    .sort((a, b) => a.localeCompare(b));
};

type PageFilters = Pick<
  Partial<SEOSegmentationFilterInput>,
  | 'activityParticipationType'
  | 'activityDaysOfWeek'
  | 'childCareAgeGroups'
  | 'activityDates'
  | 'sortBy'
>;

export const ExploreFilters: FC<ExploreFilterProps> = ({
  isMobile,
  LocationInput,
  activityCategory,
  activitySubcategory,
}) => {
  const { filters, setFilters, fetchResults } = useSegmentationFiltersContext();

  const categoryKey = getCategoryKey(activityCategory);
  const defaultCategory = ACTIVITY_CATEGORY_TITLES[categoryKey];
  const categoryOptions = Object.values(ACTIVITY_CATEGORY_TITLES).sort((a, b) =>
    a.localeCompare(b)
  );
  const initialSubcategoryOptions = getSubcategories(categoryKey);
  const [subcategoryOptions, setSubcategoryOptions] = useState(initialSubcategoryOptions);

  const applyFilters = (nextFilters: PageFilters) => {
    const { __typename: typename, location, ...restFilters } = filters ?? {};

    const zipcode = location?.zipcode ?? undefined;
    const updatedFilters = {
      zipcode,
      ...restFilters,
      ...nextFilters,
    };

    saveFilters(updatedFilters);
    setFilters(updatedFilters);
    fetchResults(updatedFilters, true);
  };

  const onApplyActivityType = (selectedActivityType: string[]) => {
    if (!selectedActivityType) return;
    const activityParticipationType: ActivityParticipationType[] = selectedActivityType.map(
      (type: any) => {
        return ACTIVITY_MAP_TO_FILTER[type] as ActivityParticipationType;
      }
    );
    applyFilters({
      activityParticipationType,
    });
  };

  const onApplyDays = (selectedDays: string[]) => {
    if (!selectedDays || isMobile) return;
    const activityDaysOfWeek: DayOfWeek[] = selectedDays.map((day: any) => {
      return DAYS_MAP_TO_FILTER[day] as DayOfWeek;
    });
    applyFilters({
      activityDaysOfWeek,
    });
  };

  const onApplyAges = (selectedAges: string[]) => {
    if (!selectedAges || isMobile) return;
    const childCareAgeGroups: ChildCareAgeGroups[] = selectedAges.map((age: any) => {
      return AGES_MAP_TO_FILTER[age] as ChildCareAgeGroups;
    });

    applyFilters({
      childCareAgeGroups,
    });
  };

  const onApplyDates = (selectedDates: Dayjs[]) => {
    if (isMobile) return;
    const dates = selectedDates ? selectedDates.map((dayJs) => dayJs.format('YYYY-MM-DD')) : [];

    applyFilters({
      activityDates: dates,
    });
  };

  const onFilterOpen = () => {
    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
      feature_name: 'Filter Viewed',
    });
  };

  const toTitleCaseWithSpace = (str: string): string => {
    // Add a space before each uppercase letter and capitalize the first letter of each word
    return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formattedSubcategory = toTitleCaseWithSpace(activitySubcategory ?? '');

  return (
    <FilterLayout LocationInput={LocationInput}>
      <FilterSelect
        defaultValue={defaultCategory}
        label="Categories"
        options={categoryOptions}
        type="single-select"
        name={FilterFieldNames.categories}
        onOpen={onFilterOpen}
        onChange={(currentCategory: string) => {
          const currentActivityKey = getCategoryKey(currentCategory);
          setSubcategoryOptions(getSubcategories(currentActivityKey));
        }}
      />
      <FilterSelect
        defaultValue={formattedSubcategory}
        label="Subcategories"
        name={FilterFieldNames.subcategories}
        options={subcategoryOptions}
        type="single-select"
        onOpen={onFilterOpen}
      />
      <MultiSelect
        label="Ages"
        options={ages}
        name={FilterFieldNames.ages}
        onOpen={onFilterOpen}
        onChange={onApplyAges}
      />

      <FilterSelect
        type="dates"
        label="Dates"
        name={FilterFieldNames.dates}
        onOpen={onFilterOpen}
        onChange={onApplyDates}
      />
      <MultiSelect
        label="Activity type"
        options={activityTypes}
        name={FilterFieldNames.activityType}
        onOpen={onFilterOpen}
        onChange={onApplyActivityType}
      />
      <MultiSelect
        label="Days"
        options={days}
        name={FilterFieldNames.days}
        onOpen={onFilterOpen}
        onChange={onApplyDays}
      />
    </FilterLayout>
  );
};
